import { Component, h, Prop, Event, EventEmitter, Element, Build } from '@stencil/core';

// Static counter for unique checkbox names
const nameCounters: Record<string, number> = {};

/**
 * Checkbox form control with an adjacent text label and optional validation.
 * Supports checked, disabled, and required states; emits change events on interaction.
 * Generates a unique name attribute when not provided to avoid collisions in repeated form layouts.
 *
 * @example
 *   <me-checkbox name="terms" required>I agree to the Terms and Conditions</me-checkbox>
 */
@Component({
  tag: 'me-checkbox',
  styleUrl: 'index.scss',
  shadow: false,
})
export class Checkbox {
  @Element() hostEl!: HTMLElement;

  /**
   * The text label that appears next to the checkbox
   * @default ''
   */
  @Prop() label: string = '';

  /**
   * The checked state of the checkbox
   * @default false
   */
  @Prop({ mutable: true }) checked: boolean = false;

  /**
   * Whether the checkbox is disabled
   * @default false
   */
  @Prop() disabled: boolean = false;

  /**
   * The name of the checkbox when used in a form
   * @default ''
   */
  @Prop() name: string = '';

  /**
   * Whether the checkbox is required in a form
   * @default false
   */
  @Prop() required: boolean = false;

  /**
   * Error state for checkbox (set by form handler)
   */
  @Prop() error?: string;

  /**
   * Error message for checkbox (set by form handler)
   */
  @Prop() errorMessage?: string;

  /**
   * Custom message for required error
   */
  @Prop() requiredMessage?: string;

  /**
   * Custom message for invalid error
   */
  @Prop() invalidMessage?: string;

  /**
   * Error type: 'required' | 'invalid' (set by form handler)
   */
  @Prop() errorType?: 'required' | 'invalid';

  /**
   * Emitted when the checkbox checked state changes
   * @event valueChange
   */
  @Event() valueChange: EventEmitter<boolean>;

  /**
   * Emitted when the checkbox error state changes
   * @event clearFieldError
   */
  @Event() clearFieldError: EventEmitter<string>;

  private inputRef?: HTMLInputElement;
  private hadFocus = false;

  componentWillLoad() {
    const baseName = this.name || 'checkbox';
    if (!nameCounters[baseName]) {
      nameCounters[baseName] = 0;
    }
    nameCounters[baseName]++;
  }

  private restoreFocusIfNeeded = () => {
    // Restore focus if it was present before render
    if (this.hadFocus && this.inputRef) {
      this.inputRef.focus();
    }
    this.hadFocus = false;
  };

  private getResolvedName(): string {
    // Find the nearest parent form with data-form-name attribute
    let parent = this.hostEl.parentElement;
    while (parent) {
      if (parent.tagName && parent.tagName.toLowerCase() === 'form' && parent.hasAttribute('data-form-name')) {
        const formName = parent.getAttribute('data-form-name');
        if (formName && this.name) {
          return `${formName}-${this.name}`;
        }
      }
      parent = parent.parentElement;
    }
    return this.name;
  }

  private handleChange = () => {
    this.checked = !this.checked; // Change component state, not DOM directly
    this.valueChange.emit(this.checked);
    this.clearFieldError.emit(this.getResolvedName());
    (this as any).hostEl?.removeAttribute('error');
    (this as any).hostEl?.removeAttribute('error-message');
    (this as any).hostEl?.removeAttribute('error-type');
    setTimeout(this.restoreFocusIfNeeded, 0);
  };

  private handleInputKeyDown = (event: KeyboardEvent) => {
    if (event.key === ' ' || event.key === 'Enter') {
      // Save that there was focus
      this.hadFocus = Build.isBrowser && document.activeElement === this.inputRef;
      event.preventDefault();
      if (this.inputRef) {
        this.handleChange();
      }
      setTimeout(this.restoreFocusIfNeeded, 0);
    }
  };

  render() {
    // Generate id only if both label and name are present
    let inputId: string | undefined = undefined;
    const resolvedName = this.getResolvedName();
    if (this.label && this.name) {
      if (Build.isBrowser && !document.getElementById(resolvedName)) {
        inputId = resolvedName;
      } else {
        inputId = resolvedName;
      }
    }
    const hasError = this.error === 'true';
    let errorText = this.errorMessage;
    if (!errorText && hasError) {
      if (this.errorType === 'required') errorText = this.requiredMessage || 'This field is required';
      else if (this.errorType === 'invalid') errorText = this.invalidMessage || 'Invalid value';
      else errorText = 'This field is invalid';
    }
    if (hasError && errorText) {
      return (
        <div class="checkbox-wrapper">
          <label
            class={{
              'checkbox': true,
              'checkbox--checked': this.checked,
              'checkbox--disabled': this.disabled,
              'checkbox--error': hasError,
            }}
            htmlFor={inputId}
          >
            <input
              type="checkbox"
              class={{
                'checkbox__input': true,
                'checkbox__input--error': hasError,
              }}
              {...(inputId ? { id: inputId } : {})}
              name={resolvedName}
              checked={this.checked}
              disabled={this.disabled}
              required={this.required}
              ref={el => (this.inputRef = el)}
              onChange={this.handleChange}
              tabIndex={this.disabled ? -1 : 0}
              onKeyDown={this.handleInputKeyDown}
            />
            <span class="checkbox__box">
              {this.checked && (
                <svg class="checkbox__icon" width="14" height="11" viewBox="0 0 14 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 5L5 9L13 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              )}
            </span>
            <span class="checkbox__label">{this.label || <slot />}</span>
          </label>
          <me-text variant="small" class="form-error-message">
            {errorText}
          </me-text>
        </div>
      );
    }
    return (
      <label
        class={{
          'checkbox': true,
          'checkbox--checked': this.checked,
          'checkbox--disabled': this.disabled,
          'checkbox--error': hasError,
        }}
        htmlFor={inputId}
      >
        <input
          type="checkbox"
          class={{
            'checkbox__input': true,
            'checkbox__input--error': hasError,
          }}
          {...(inputId ? { id: inputId } : {})}
          name={resolvedName}
          checked={this.checked}
          disabled={this.disabled}
          required={this.required}
          ref={el => (this.inputRef = el)}
          onChange={this.handleChange}
          tabIndex={this.disabled ? -1 : 0}
          onKeyDown={this.handleInputKeyDown}
        />
        <span class="checkbox__box">
          {this.checked && (
            <svg class="checkbox__icon" width="14" height="11" viewBox="0 0 14 11" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 5L5 9L13 1" stroke="currentColor" stroke-width="2" stroke-linecap="butt" stroke-linejoin="miter" />
            </svg>
          )}
        </span>
        <span class="checkbox__label">{this.label || <slot />}</span>
      </label>
    );
  }
}
